class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleOverlayClick = this.close.bind(this);
    this.handleRoadmapCartUpdate = this.handleRoadmapCartUpdate.bind(this);
    this.handleLoyaltyProcessingEnd = this.handleLoyaltyProcessingEnd.bind(this);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayListener();
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    window.addEventListener('roadmap:cart:update', this.handleRoadmapCartUpdate);
    window.addEventListener('loyalty-dropdown:processing-end', this.handleLoyaltyProcessingEnd);
  }

  disconnectedCallback() {
    window.removeEventListener('roadmap:cart:update', this.handleRoadmapCartUpdate);
    window.removeEventListener('loyalty-dropdown:processing-end', this.handleLoyaltyProcessingEnd);
  }

  handleLoyaltyProcessingEnd() {
    setTimeout(() => {
      this.fetchAndUpdateDrawerSections();
    }, 500);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.bindOverlayListener();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  bindOverlayListener() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;

    overlay.removeEventListener('click', this.handleOverlayClick);
    overlay.addEventListener('click', this.handleOverlayClick);
  }

  handleRoadmapCartUpdate(event) {
    // Sync the drawer when the roadmap integration signals a cart refresh.
    const cartData = event && event.detail ? event.detail.cartData : undefined;

    if (cartData && cartData.sections) {
      this.updateDrawerSections(cartData.sections, cartData.item_count);
      return;
    }

    this.fetchAndUpdateDrawerSections();
  }

  fetchAndUpdateDrawerSections() {
    const sectionIds = this.getSectionsToRender()
      .map((section) => section.id)
      .join(',');

    fetch(`${routes.cart_url}?sections=${sectionIds}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to refresh cart drawer sections');
        }
        return response.json();
      })
      .then((sectionsHtml) => {
        this.updateDrawerSections(sectionsHtml);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  updateDrawerSections(sectionsHtml, itemCount) {
    if (!sectionsHtml) return;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const htmlFragment = sectionsHtml[section.id];
      if (!sectionElement || !htmlFragment) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(htmlFragment, section.selector);
    });

    if (typeof itemCount === 'number') {
      this.classList.toggle('is-empty', itemCount === 0);
    } else if (sectionsHtml['cart-drawer']) {
      const drawerSection = this.getSectionDOM(sectionsHtml['cart-drawer'], '.shopify-section');
      const innerDrawer = drawerSection ? drawerSection.querySelector('.drawer__inner') : null;
      if (innerDrawer) {
        this.classList.toggle('is-empty', innerDrawer.classList.contains('is-empty'));
      }
    }

    this.bindOverlayListener();
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
